import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Mail } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { useSettings } from "../lib/settings";

export default function ForgotPassword() {
  const { settings } = useSettings();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[color:var(--atech-bg)] px-4">
      <div className="w-full max-w-sm fade-up">
        {/* Logo / wordmark */}
        <div className="mb-8 text-center">
          <div className="font-display text-2xl text-[color:var(--atech-text)] mb-1">
            {settings.practice_name}
          </div>
          <div className="label text-[color:var(--atech-text-muted)]">{settings.portal_name}</div>
        </div>

        <div className="card-surface p-8">
          {sent ? (
            /* ── Success state ── */
            <div className="text-center py-4" data-testid="forgot-password-success">
              <div className="w-12 h-12 rounded-full bg-[color:var(--atech-copper)]/10 flex items-center justify-center mx-auto mb-4">
                <Mail size={22} className="text-[color:var(--atech-copper)]" />
              </div>
              <h2 className="font-display text-xl text-[color:var(--atech-text)] mb-2">
                Check your inbox
              </h2>
              <p className="text-sm text-[color:var(--atech-text-dim)] leading-relaxed">
                If that email address is registered, we've sent you a password reset link.
                It expires in 60 minutes.
              </p>
              <p className="text-xs text-[color:var(--atech-text-muted)] mt-3">
                No email? Check your spam folder or contact {settings.practice_name} directly.
              </p>
            </div>
          ) : (
            /* ── Email form ── */
            <>
              <h2 className="font-display text-xl text-[color:var(--atech-text)] mb-1">
                Forgot your password?
              </h2>
              <p className="text-sm text-[color:var(--atech-text-dim)] mb-6 leading-relaxed">
                Enter the email address on your account and we'll send you a reset link.
              </p>

              {error && (
                <div
                  className="text-[#F87171] text-sm mb-4 p-3 bg-[#F87171]/8 rounded-[2px]"
                  data-testid="forgot-error"
                >
                  {error}
                </div>
              )}

              <form onSubmit={submit} className="space-y-4" data-testid="forgot-password-form">
                <div>
                  <label className="label text-[color:var(--atech-text-muted)] mb-1.5 block">
                    Email address
                  </label>
                  <input
                    type="email"
                    required
                    autoFocus
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full bg-[color:var(--atech-bg)] border border-[color:var(--atech-border)] rounded-[2px] px-4 py-3 text-sm focus:outline-none focus:border-[color:var(--atech-copper)] transition-colors"
                    placeholder="you@example.com"
                    data-testid="forgot-email-input"
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="btn-copper w-full py-3 text-sm disabled:opacity-60"
                  data-testid="forgot-submit-btn"
                >
                  {loading ? "Sending…" : "Send reset link"}
                </button>
              </form>
            </>
          )}
        </div>

        <Link
          to="/login"
          className="flex items-center justify-center gap-2 mt-6 text-xs text-[color:var(--atech-text-muted)] hover:text-[color:var(--atech-text)] transition-colors"
          data-testid="back-to-login"
        >
          <ArrowLeft size={13} /> Back to sign in
        </Link>
      </div>
    </div>
  );
}
